/**
 * Bundle Builder — bundle-builder.js
 * Best Day Brewing | Fetches bundle config from Awtomic and renders dynamically
 */

(function () {
  'use strict';

  // ─── Analytics ───────────────────────────────────────────────────────────
  let _bundleStartTime = null;

  function trackEvent(eventName, params) {
    params = params || {};
    if (window.gtag && typeof window.gtag === 'function') {
      try { window.gtag('event', eventName, params); } catch (e) {}
    }
    if (window.dataLayer && Array.isArray(window.dataLayer)) {
      try { window.dataLayer.push(Object.assign({ event: eventName }, params)); } catch (e) {}
    }
  }

  // ─── State ───────────────────────────────────────────────────────────────
  const state = {
    minSize: 0,
    packCans: 0,
    discountAmount: 0,
    discountType: 'percentage',
    discountCode: '',
    selectedFreq: 'one-time',
    selectedSellingPlanId: null,
    awtomicBundle: null,   // full bundle object from reactInit
    bundleVariantGqlId: null, // box-size variant (Awtomic _variantId), not selected product
    sellingPlans: [],      // [{id, name, price, compareAtPrice, ...}]
    selections: [],        // [{id, variantId, variantGqlId, title, image, price, qty}]
  };

  // ─── DOM refs ─────────────────────────────────────────────────────────────
  let $sizeBtns, $slots, $count, $savingsEl, $barFill;
  let $stickyCount, $stickySavings, $stickyFill, $ctaBtn, $ctaSidebarBtn;
  let $freqSelect, $onetimePrice, $subPrice, $subCompare, $freqBadge;

  // ─── Helpers ──────────────────────────────────────────────────────────────
  function filledCount() {
    return state.selections.reduce(function (acc, s) { return acc + s.qty * s.packSize; }, 0);
  }

  function isFull() {
    return filledCount() >= state.packCans;
  }

  function remainingSlots() {
    return Math.max(0, state.packCans - filledCount());
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function totalPrice() {
    const total = totalPriceCompare();
    if (state.discountType === 'percentage') {
      return total * (100 - (state.discountAmount || 0)) / 100
    } else {
      return total - (state.discountAmount || 0) * 100;
    }
  }

  function totalPriceCompare() {
    let total = 0;
    state.selections.forEach(function (sel) { total += sel.price * sel.qty; });
    return total
  }

  // ─── Pack size selector ──────────────────────────────────────────────────
  function renderPackSizes() {
    if (!$sizeBtns) return;
    while ($sizeBtns.firstChild) $sizeBtns.removeChild($sizeBtns.firstChild);

    const buttonsHtml = state.awtomicBundle.Settings.map((setting, idx) => {
      return `
        <button
          class="bb-size-btn ${(parseInt(setting.Size) === state.minSize ? ' is-active' : '')}"
          type="button"
          role="radio"
          aria-checked="${parseInt(setting.Size) === state.minSize ? 'true' : 'false'}"
          data-index="${idx}"
        >
          <div class="bb-size-btn-badge" ${!!setting.DynamicDiscountAmount ? '' : 'hidden'}>
            <div class="bb-size-btn-badge__inner">
              <svg width="42" height="41" viewBox="0 0 42 41" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.2567 2.80121C18.0178 -0.933736 23.3548 -0.933736 24.1159 2.80121C24.6376 5.36116 27.6784 6.46793 29.7235 4.84222C32.7074 2.47032 36.7957 5.90088 34.978 9.25126C33.7321 11.5476 35.3501 14.3501 37.9618 14.4193C41.7721 14.5203 42.6989 19.7762 39.1529 21.1743C36.7224 22.1326 36.1605 25.3194 38.1166 27.0512C40.9706 29.5778 38.3021 34.1998 34.687 32.9915C32.2092 32.1633 29.7303 34.2433 30.1156 36.8273C30.6778 40.5973 25.6627 42.4227 23.67 39.1733C22.3043 36.9462 19.0683 36.9462 17.7025 39.1733C15.7099 42.4227 10.6947 40.5973 11.2569 36.8273C11.6423 34.2433 9.16338 32.1633 6.68556 32.9915C3.07044 34.1998 0.401938 29.5778 3.25593 27.0512C5.21208 25.3194 4.65016 22.1326 2.21969 21.1743C-1.32635 19.7762 -0.399588 14.5203 3.41078 14.4193C6.02243 14.3501 7.64041 11.5476 6.39454 9.25126C4.57682 5.90088 8.6652 2.47032 11.649 4.84222C13.6942 6.46793 16.735 5.36116 17.2567 2.80121Z" fill="#F1BD26"/>
              </svg>
              <div class="bb-size-btn-badge__content">
                <span class="bb-size-btn-badge__title">Save</span>
                <span class="bb-size-btn-badge__subtitle">${setting.DynamicDiscountType === 'fixed' ? '$' : ''}${setting.DynamicDiscountAmount}${setting.DynamicDiscountType === 'percentage' ? '%' : ''}</span>
              </div>
            </div>
          </div>
          ${setting.Label}
        </button>
      `
    }).join('')

    $sizeBtns.innerHTML = buttonsHtml

    $sizeBtns.querySelectorAll('.bb-size-btn').forEach(btn => btn.addEventListener('click', () => {
      handleSizeChange(state.awtomicBundle.Settings[parseInt(btn.dataset.index)]);
    }))
  }

  function handleSizeChange(ps, autoBumped) {
    state.minSize = parseInt(ps.Size);
    state.packCans = state.minSize * 6;
    state.bundleVariantGqlId = ps.VariantId;
    state.discountAmount = parseInt(ps.DynamicDiscountAmount, 10) || 0;
    state.discountType = ps.DynamicDiscountType || 'percentage';
    state.discountCode = ps.DynamicDiscountCode || '';

    localStorage.setItem('bundleSize', state.minSize)

    trackEvent('bundle_size_select', {
      pack_size: state.packCans,
      auto_bumped: !!autoBumped,
    });

    // Trim selections if over new limit
    while (filledCount() > state.packCans) {
      const last = state.selections[state.selections.length - 1];
      last.qty--;
      if (last.qty <= 0) state.selections.pop();
      const card = document.querySelector('.bb-product-card[data-product-id="' + last.id + '"]');
      if (card) syncCard(card);
    }

    renderPackSizes();
    renderSlots();
    updateProgress();
    updateAddButtons();
  }

  // ─── Fetch Awtomic bundle config ────────────────────────────────────────
  function fetchAwtomicConfig(bundleId) {
    return fetch('/tools/bundle-subscriptions/bundle/' + bundleId)
      .then(function (resp) {
        if (!resp.ok) throw new Error('Awtomic fetch failed: ' + resp.status);
        return resp.text();
      })
      .then(function (html) {
        // Find window.reactInit assignment and extract JSON via brace-counting
        const marker = 'window.reactInit';
        const idx = html.indexOf(marker);
        if (idx === -1) throw new Error('Could not find reactInit in Awtomic page');

        // Find the opening brace
        const braceStart = html.indexOf('{', idx);
        if (braceStart === -1) throw new Error('Could not find reactInit JSON start');

        // Count braces to find the matching closing brace
        let depth = 0;
        let i = braceStart;
        while (i < html.length) {
          if (html[i] === '{') depth++;
          else if (html[i] === '}') {
            depth--;
            if (depth === 0) break;
          }
          i++;
        }

        if (depth !== 0) throw new Error('Unbalanced braces in reactInit JSON');

        const jsonStr = html.substring(braceStart, i + 1);
        return JSON.parse(jsonStr);
      });
  }

  // ─── Fetch product details from Shopify ─────────────────────────────────
  function fetchProductDetails(handles) {
    const promises = handles.map(function (handle) {
      return fetch(`/products/${handle}?view=json`)
        .then(function (resp) {
          if (!resp.ok) return null;
          return resp.json();
        })
        .catch(function () { return null; });
    });
    return Promise.all(promises).then(function (results) {
      return results.filter(Boolean);
    });
  }

  // ─── Extract selling plans from product data ────────────────────────────
  function extractSellingPlans(products) {
    const plans = [];

    for (let i = 0; i < products.length; i++) {
      const pd = products[i];
      if (!pd || !pd.selling_plan_groups) continue;

      for (let g = 0; g < pd.selling_plan_groups.length; g++) {
        const group = pd.selling_plan_groups[g];
        if (!group.selling_plans) continue;

        for (let s = 0; s < group.selling_plans.length; s++) {
          const sp = group.selling_plans[s];
          if (plans.some(function (p) { return p.id === sp.id; })) continue;

          let alloc = null;
          if (pd.variants && pd.variants[0] && pd.variants[0].selling_plan_allocations) {
            alloc = pd.variants[0].selling_plan_allocations.find(function (a) {
              return a.selling_plan_id === sp.id;
            });
          }

          plans.push({
            id: sp.id,
            name: sp.name,
            price: alloc ? alloc.price : 0,
            compareAtPrice: alloc ? alloc.compare_at_price : 0,
            perDeliveryPrice: alloc ? alloc.per_delivery_price : 0,
            discountValue: sp.price_adjustments && sp.price_adjustments[0] ? sp.price_adjustments[0].value : 0,
            discountType: sp.price_adjustments && sp.price_adjustments[0] ? sp.price_adjustments[0].value_type : 'percentage',
          });
        }
        return plans;
      }
    }
    return plans;
  }

  // ─── Render product cards from Shopify product data ──────────────────────
  function renderProducts(products) {
    const containers = document.querySelectorAll('.bb-products-col');

    for (const container of containers) {
      const loading = container.querySelector('.bb-loading');

      if (products.length === 0) {
        const msg = document.createElement('p');
        msg.className = 'bb-loading-text';
        msg.textContent = 'No products found in this bundle.';
        container.appendChild(msg);
        return;
      }

      if (loading) loading.remove();

      const gridHtml = state.awtomicBundle.Sections.map(section => {
        const sectionVariants = section.Products.map(item => {
          const product = products.find(p => p.handle === item.Handle)
          if (!product) return '';

          const variant = product.variants.find(v => v.gid === item.Variants[0].VariantId)
          return variant
        })

        const isLargePacks = sectionVariants.every(variant => parseInt(variant.pack_size) === 12)

        return `
          <div class="bb-products-group">
            ${state.awtomicBundle.Sections.length > 1 ? `
              <div class="bb-products-group__header">
                <h2 class="bb-group-title">${section.Name}</h2>
              </div>
              `: ''}
            <div class="bb-product-group bb-product-group--grid ${isLargePacks ? 'bb-product-group--large' : ''}">
              ${
                section.Products.map(item => {
                  const product = products.find(p => p.handle === item.Handle)

                  if (!product) return '';

                  const variant = product.variants.find(v => v.gid === item.Variants[0].VariantId)

                  if (!!product && !!variant) {
                    return `
                      <div
                        class="bb-product-card bb-product-card--grid"
                        data-product-id="${product.id}"
                        data-variant-id="${variant.id}"
                        data-variant-gql-id="gid://shopify/ProductVariant/${variant.id}"
                        data-product-title="${product.title}"
                        data-product-image="${product.featured_image}"
                        data-price="${variant.price}"
                        data-pack-size="${variant.pack_size}"
                      >
                        <div class="bb-card-img-wrap">
                          <img
                            src="${product.featured_image}"
                            class="bb-card-img"
                            width="250"
                            loading="lazy"
                          >
                          <span class="bb-card-badge" hidden></span>
                        </div>

                        <div class="bb-card-body">
                          ${product.title}
                        </div>
                      </div>
                    `
                  } else {
                    return ''
                  }
                }).join('')
              }
            </div>
          </div>
        `
      }).join('')

      container.innerHTML = gridHtml
    }
  }

  // ─── Slot rendering ───────────────────────────────────────────────────────
  function renderSlots() {
    for (const slots of $slots) {
      // Expand selections into individual slot entries
      const expanded = [];
      state.selections.forEach(function (sel) {
        for (let i = 0; i < sel.qty; i++) {
          expanded.push(sel);
        }
      });

      // Build slot elements via DOM API
      while (slots.firstChild) slots.removeChild(slots.firstChild);

      for (let i = 0; i < state.minSize; i++) {
        const sel = expanded[i];
        const slotEl = document.createElement('div');

        if (sel) {
          slotEl.className = 'bb-slot bb-slot--filled';
          slotEl.dataset.productId = sel.id;

          if (sel.image) {
            const img = document.createElement('img');
            img.className = 'bb-slot__img';
            img.src = sel.image;
            img.alt = sel.title;
            img.loading = 'lazy';
            slotEl.appendChild(img);
          }

          const removeBtn = document.createElement('button');
          removeBtn.className = 'bb-slot__remove';
          removeBtn.dataset.productId = sel.id;
          removeBtn.setAttribute('aria-label', 'Remove one ' + sel.title);
          // Static SVG icon (no user data)
          const xSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
          xSvg.setAttribute('width', '10');
          xSvg.setAttribute('height', '10');
          xSvg.setAttribute('viewBox', '0 0 10 10');
          xSvg.setAttribute('fill', 'none');
          const l1 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
          l1.setAttribute('x1', '1'); l1.setAttribute('y1', '1');
          l1.setAttribute('x2', '9'); l1.setAttribute('y2', '9');
          l1.setAttribute('stroke', 'currentColor'); l1.setAttribute('stroke-width', '2');
          l1.setAttribute('stroke-linecap', 'round');
          const l2 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
          l2.setAttribute('x1', '9'); l2.setAttribute('y1', '1');
          l2.setAttribute('x2', '1'); l2.setAttribute('y2', '9');
          l2.setAttribute('stroke', 'currentColor'); l2.setAttribute('stroke-width', '2');
          l2.setAttribute('stroke-linecap', 'round');
          xSvg.appendChild(l1);
          xSvg.appendChild(l2);
          removeBtn.appendChild(xSvg);

          removeBtn.addEventListener('click', (function (productId) {
            return function (e) {
              e.stopPropagation();
              removeOneFromSelection(productId);
            };
          })(sel.id));
          slotEl.appendChild(removeBtn);
        } else {
          slotEl.className = 'bb-slot bb-slot--empty';
          const plusSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
          plusSvg.setAttribute('class', 'bb-slot__plus');
          plusSvg.setAttribute('viewBox', '0 0 24 24');
          plusSvg.setAttribute('fill', 'none');
          const r1 = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
          r1.setAttribute('x', '11'); r1.setAttribute('y', '4');
          r1.setAttribute('width', '2'); r1.setAttribute('height', '16');
          r1.setAttribute('rx', '1'); r1.setAttribute('fill', '#cad3d8');
          const r2 = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
          r2.setAttribute('x', '4'); r2.setAttribute('y', '11');
          r2.setAttribute('width', '16'); r2.setAttribute('height', '2');
          r2.setAttribute('rx', '1'); r2.setAttribute('fill', '#cad3d8');
          plusSvg.appendChild(r1);
          plusSvg.appendChild(r2);
          slotEl.appendChild(plusSvg);
        }

        slots.appendChild(slotEl);
      }
    }

    document.querySelectorAll('.bb-slots').forEach(slots => {
      slots.classList[isFull() ? 'add' : 'remove']('is-full')
    })
  }

  // ─── Progress update ──────────────────────────────────────────────────────
  function updateProgress() {
    const filled = filledCount();
    const pct = state.packCans > 0 ? Math.min((filled / state.packCans) * 100, 100) : 0;
    const complete = filled >= state.packCans;

    const countText = filled + ' of ' + state.packCans;

    // Sidebar progress
    if ($count) $count.textContent = countText;
    if ($savingsEl) {
      $savingsEl.textContent = `${state.discountType === 'fixed' ? '$' : ''}${filled > 0 ? state.discountAmount : 0}${state.discountType === 'percentage' ? '%' : ''} off`;
      $savingsEl.classList[state.discountAmount === 0 ? 'add' : 'remove']('hidden')
    }
    if ($barFill) $barFill.style.width = pct + '%';

    // Sidebar CTA
    if ($ctaSidebarBtn) {
      $ctaSidebarBtn.disabled = !complete;
      if (complete) {
        $ctaSidebarBtn.textContent = 'Add to Cart';
      } else {
        $ctaSidebarBtn.textContent = 'Complete your pack';
      }
    }

    // Mobile sticky
    if ($stickyCount) $stickyCount.textContent = countText;
    if ($stickyFill) $stickyFill.style.width = pct + '%';

    // Mobile CTA
    if ($ctaBtn) {
      $ctaBtn.disabled = !complete;
      $ctaBtn.textContent = complete ? 'Add to Cart' : 'Complete your pack';
    }

    updateFrequencyPrices();
  }

  // ─── Frequency / Selling Plans ────────────────────────────────────────────
  function initFrequency() {
    if (state.sellingPlans.length === 0) return;

    // Populate select dropdown
    if ($freqSelect) {
      state.sellingPlans.forEach(function (sp) {
        const opt = document.createElement('option');
        opt.value = sp.id;
        opt.textContent = sp.name.replace(/Deliver every/g, '');
        $freqSelect.appendChild(opt);
      });
    }

    // Set frequency badge (discount %)
    if ($freqBadge) {
      const plan = state.sellingPlans[0];
      if (plan.discountValue > 0) {
        $freqBadge.textContent = plan.discountValue + '% off';
      } else if (plan.compareAtPrice > 0 && plan.price < plan.compareAtPrice) {
        const pctOff = Math.round((1 - plan.price / plan.compareAtPrice) * 100);
        $freqBadge.textContent = pctOff + '% off';
      }
    }

    // Bind frequency option clicks
    document.querySelectorAll('.bb-freq-option').forEach(function ($opt) {
      $opt.classList[$opt.getAttribute('data-freq') === state.selectedFreq ? 'add' : 'remove']('is-active')

      $opt.addEventListener('click', function () {
        handleFreqChange($opt);
      });
    });

    // Bind select changes
    if ($freqSelect) {
      if (state.selectedSellingPlanId) {
        $freqSelect.value = state.selectedSellingPlanId
      }

      $freqSelect.addEventListener('change', function () {
        state.selectedSellingPlanId = parseInt(this.value, 10);
        localStorage.setItem('sellingPlanId', state.selectedSellingPlanId)
        updateFrequencyPrices();
        updateProgress();
      });
      $freqSelect.addEventListener('click', function (e) {
        e.stopPropagation();
      });
    }

    updateFrequencyPrices();
  }

  function handleFreqChange($opt) {
    document.querySelectorAll('.bb-freq-option').forEach(function (o) {
      o.classList.remove('is-active');
    });
    $opt.classList.add('is-active');

    const freq = $opt.dataset.freq;
    state.selectedFreq = freq;

    if (freq === 'one-time') {
      state.selectedSellingPlanId = null;
    } else if ($freqSelect) {
      state.selectedSellingPlanId = parseInt($freqSelect.value, 10);
    }

    localStorage.setItem('sellingPlanId', state.selectedSellingPlanId)

    updateFrequencyPrices();
    updateProgress();
  }

  function updateFrequencyPrices() {
    const total = totalPrice();
    if ($onetimePrice) {
      $onetimePrice.textContent = formatMoney(total);
    }

    if (document.querySelector('#bb-onetime-compare')) {
      document.querySelector('#bb-onetime-compare').textContent = formatMoney(totalPriceCompare())
      document.querySelector('#bb-onetime-compare').classList[totalPrice() === totalPriceCompare() ? 'add' : 'remove']('hidden')
    }

    if (state.sellingPlans.length > 0) {
      const plan = state.sellingPlans.find(function (p) {
        return p.id === state.selectedSellingPlanId;
      }) || state.sellingPlans[0];

      if (plan.compareAtPrice > 0) {
        const ratio = plan.price / plan.compareAtPrice;
        let discountedTotal = Math.round(totalPriceCompare() * ratio);

        if (state.discountType === 'fixed') {
          discountedTotal -= state.discountAmount * 100
        } else {
          discountedTotal = discountedTotal * (100 - state.discountAmount) / 100
        }

        if ($subPrice) $subPrice.textContent = formatMoney(discountedTotal);
        if ($subCompare) $subCompare.textContent = formatMoney(total);
      } else {
        if ($subPrice) $subPrice.textContent = formatMoney(total);
        if ($subCompare) $subCompare.textContent = '';
      }
    }
  }

  // ─── Add / remove ─────────────────────────────────────────────────────────
  function addToSelection(card) {

    if (isFull()) {
      return
    }

    const id = card.dataset.productId;
    const existing = state.selections.find(function (s) { return s.id === id; });
    if (existing) {
      existing.qty++;
    } else {
      state.selections.push({
        id: id,
        variantId: card.dataset.variantId,
        variantGqlId: card.dataset.variantGqlId,
        title: card.dataset.productTitle,
        image: card.dataset.productImage,
        price: parseInt(card.dataset.price, 10) || 0,
        qty: 1,
        packSize: parseInt(card.dataset.packSize)
      });
    }

    localStorage.setItem('bundleSelections', JSON.stringify(state.selections))

    document.querySelector('.bb-sticky-selections').setAttribute('open', true)

    trackEvent('bundle_slot_filled', {
      product_title: card.dataset.productTitle,
      product_id: card.dataset.productId,
      variant_id: card.dataset.variantId,
      slot_number: filledCount(),
      pack_size: state.packCans,
    });

    syncCard(card);
    renderSlots();
    updateProgress();
    updateAddButtons();
  }

  function removeOneFromSelection(id) {
    const cards = document.querySelectorAll('.bb-product-card[data-product-id="' + id + '"]');
    const existing = state.selections.find(function (s) { return s.id === id; });
    if (!existing) return;

    trackEvent('bundle_slot_removed', {
      product_title: existing.title,
      product_id: id,
      variant_id: existing.variantId,
      slot_number: filledCount(),
      pack_size: state.packCans,
    });

    existing.qty--;
    if (existing.qty <= 0) {
      state.selections = state.selections.filter(function (s) { return s.id !== id; });
    }

    localStorage.setItem('bundleSelections', JSON.stringify(state.selections))

    for (const card of cards) {
      syncCard(card)
    }

    renderSlots();
    updateProgress();
    updateAddButtons();
  }

  function syncCard(card) {
    const id = card.dataset.productId;
    const sel = state.selections.find(function (s) { return s.id === id; });
    const qty = sel ? sel.qty : 0;
    const badge = card.querySelector('.bb-card-badge');

    if (qty > 0) {
      card.classList.add('is-selected');
      if (badge) {
        badge.hidden = false;
        badge.textContent = qty;
      }
    } else {
      card.classList.remove('is-selected');
      if (badge) badge.hidden = true;
    }
  }

  function updateAddButtons() {
    const maxSetting = state.awtomicBundle.Settings.reduce(function(prev, current) {
      return (prev && parseInt(prev.Size) > parseInt(current.Size)) ? prev : current
    })

    const availableCount = parseInt(maxSetting.Size) * 6
    const remainingPacks = availableCount - filledCount()

    document.querySelectorAll('.bb-product-card').forEach(function (card) {
      const sel = state.selections.find(function (s) { return s.id === card.dataset.productId; });
      const isSelected = sel && sel.qty > 0;

      if (remainingPacks < parseInt(card.dataset.packSize) && !isSelected) {
        card.classList.add('is-disabled');
        card.setAttribute('aria-disabled', true);
      } else {
        card.classList.remove('is-disabled');
        card.removeAttribute('aria-disabled');
      }
    });
  }

  // ─── Bind product card events (called after dynamic render) ─────────────
  function bindProductCards() {
    document.querySelectorAll('.bb-product-card').forEach(function (card) {
      card.addEventListener('click', function () {
        if (card.classList.contains('is-disabled')) return;
        addToSelection(card);
      });
      card.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (card.classList.contains('is-disabled')) return;
          addToSelection(card);
        }
      });
    });
  }

  // ─── Add to cart — Awtomic integration ────────────────────────────────────
  async function handleComplete() {
    if (!isFull() || !state.awtomicBundle) return;

    if (_bundleStartTime) {
      trackEvent('bundle_completion_time', {
        duration_seconds: Math.round((Date.now() - _bundleStartTime) / 1000),
        pack_size: state.packCans,
        frequency: state.selectedFreq,
      });
    }

    const bundle = state.awtomicBundle;

    localStorage.removeItem('bundleSize');
    localStorage.removeItem('sellingPlanId');
    localStorage.removeItem('bundleSelections');

    // Generate unique bundle ID
    let bundleUid = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
          const r = Math.random() * 16 | 0;
          return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
        });

    const urlParams = new URLSearchParams(window.location.search)
    if (!!urlParams.get('bundle_id')) {
      bundleUid = urlParams.get('bundle_id')

      const cart = await (await fetch('/cart.js')).json()

      const bundleKeys = cart.items.filter(item => item?.properties?._bundleUid === bundleUid)
        .map(item => item.key)
      const updates = {}
      bundleKeys.forEach(key => updates[key] = 0)

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates })
      }).then(res => res.json())

      window.history.pushState({}, document.title, window.location.pathname)
    }

    const items = state.selections.map(function (sel) {
      const qty = state.selections.map(s => s.qty)
        .reduce((a, b) => a + b, 0)

      const item = {
        id: parseInt(sel.variantId, 10),
        quantity: sel.qty,
        properties: {
          '_bundleUid': bundleUid,
          '_bundleHandle': bundle.Handle,
          '_bundleType': 'dynamicBaB',
          '_bundleLabel': state.packCans + ' Pack',
          '_bundleProductId': bundle.ProductId,
          '_bundleName': bundle.Name,
          '_bundleBoxMinSize': String(state.minSize),
          '_variantId': state.bundleVariantGqlId,
          '_bundleInfo': `${state.packCans}-pack-bundle_${qty}`,
          '_packSize': sel.packSize
        },
      };
      if (state.selectedSellingPlanId) {
        item.selling_plan = state.selectedSellingPlanId;
      }
      return item;
    });

    if (!items.length) return;

    // Disable CTA buttons
    const btns = [
      document.getElementById('bb-cta-sidebar-btn'),
      document.getElementById('bb-cta-btn'),
    ].filter(Boolean);
    btns.forEach(function (b) { b.disabled = true; b.textContent = 'Adding\u2026'; });

    try {
      let resp = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items }),
      });

      if (!resp.ok) throw new Error('Cart add failed');

      if (!!state.discountCode) {
        resp = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            discount: state.discountCode
          })
        })

        if (!resp.ok) throw new Error('Discount application failed');
      }

      const cart = await (await fetch('/cart.js')).json();

      // Open the fly-in cart drawer
      if (window.wetheme && typeof window.wetheme.toggleRightDrawer === 'function') {
        window.wetheme.toggleRightDrawer('cart', true, { cart: cart });
      } else {
        document.dispatchEvent(new CustomEvent('wetheme-toggle-right-drawer', {
          detail: { type: 'cart', open: true, data: { cart: cart } },
        }));
      }

      // Reset selections
      state.selections = [];
      document.querySelectorAll('.bb-product-card').forEach(function (c) { syncCard(c); });
      renderSlots();
      updateProgress();
      updateAddButtons();

    } catch (err) {
      console.error('[BundleBuilder] Cart error:', err);
      btns.forEach(function (b) { b.disabled = false; });
      updateProgress();
    }
  }

  // ─── Init ─────────────────────────────────────────────────────────────────
  function init() {
    // Parse minimal config (just bundleId)
    const configEl = document.getElementById('bb-config');
    let bundleId = null;
    if (configEl) {
      try {
        const cfg = JSON.parse(configEl.textContent);
        bundleId = cfg.bundleId;
      } catch (e) {
        console.error('[BundleBuilder] Config parse error', e);
      }
    }

    if (!bundleId) {
      const containers = document.querySelectorAll('.bb-products-col');
      for (const container of containers) {
        const loading = container.querySelector('.bb-loading');
        if (loading) {
          const text = loading.querySelector('.bb-loading-text');
          if (text) text.textContent = 'No bundle ID configured. Add it in the theme editor.';
          const spinner = loading.querySelector('.bb-loading-spinner');
          if (spinner) spinner.style.display = 'none';
        }
      }
      return;
    }

    // Cache DOM refs
    $sizeBtns = document.getElementById('bb-size-btns');
    $slots = document.querySelectorAll('.bb-slots');
    $count = document.getElementById('bb-count');
    $savingsEl = document.getElementById('bb-savings');
    $barFill = document.getElementById('bb-bar-fill');
    $stickyCount = document.getElementById('bb-sticky-count');
    $stickySavings = document.getElementById('bb-sticky-savings');
    $stickyFill = document.getElementById('bb-sticky-fill');
    $ctaBtn = document.getElementById('bb-cta-btn');
    $ctaSidebarBtn = document.getElementById('bb-cta-sidebar-btn');
    $freqSelect = document.getElementById('bb-freq-select');
    $onetimePrice = document.getElementById('bb-onetime-price');
    $subPrice = document.getElementById('bb-sub-price');
    $subCompare = document.getElementById('bb-sub-compare');
    $freqBadge = document.getElementById('bb-freq-badge');

    // CTA buttons
    if ($ctaBtn) $ctaBtn.addEventListener('click', handleComplete);
    if ($ctaSidebarBtn) $ctaSidebarBtn.addEventListener('click', handleComplete);

    // Fetch Awtomic config, then fetch product details, then boot
    fetchAwtomicConfig(bundleId)
      .then(function (awtomicData) {
        // Store bundle metadata
        state.awtomicBundle = awtomicData.bundle;

        // Extract settings
        var settings = awtomicData.bundle.Settings && awtomicData.bundle.Settings[0];

        if (!!localStorage.getItem('bundleSize')) {
          settings = awtomicData.bundle.Settings.find(setting => +setting.Size === +localStorage.getItem('bundleSize'))
        }

        const urlParams = new URLSearchParams(window.location.search)
        if (!!urlParams.get('bundle_id')) {
          const bundleItems = urlParams.getAll('bundle[]')
          const packCans = bundleItems.map(item => item.split(':').pop())
            .map(qty => Number(qty))
            .reduce((a, b) => a + b, 0)
          const minSize = packCans / 6

          settings = awtomicData.bundle.Settings.find(setting => +setting.Size === minSize)
        }


        if (settings) {
          state.minSize = parseInt(settings.Size, 10) || 4;
          state.packCans = state.minSize * 6;
          state.bundleVariantGqlId = settings.VariantId;
          state.discountAmount = parseInt(settings.DynamicDiscountAmount, 10) || 0;
          state.discountType = settings.DynamicDiscountType || 'percentage';
          state.discountCode = settings.DynamicDiscountCode || '';
        }

        // Get product handles from Awtomic sections

        const sections = awtomicData.productsInSections || awtomicData.bundle.Sections || [];
        const handles = sections.flatMap(section => (section.products || section.Products).map(item => item.Handle || item.handle))

        if (handles.length === 0) {
          throw new Error('No products found in Awtomic bundle');
        }

        // Fetch full product data from Shopify
        return fetchProductDetails(handles);
      })
      .then(function (products) {
        // Extract selling plans from product data
        state.sellingPlans = extractSellingPlans(products);
        state.selectedFreq = 'one-time'

        if (!!localStorage.getItem('sellingPlanId') && localStorage.getItem('sellingPlanId') !== 'null') {
          state.selectedSellingPlanId = +localStorage.getItem('sellingPlanId')
          state.selectedFreq = 'subscribe'
        }

        if (!!localStorage.getItem('bundleSelections')) {
          state.selections = JSON.parse(localStorage.getItem('bundleSelections'))
        }

        const urlParams = new URLSearchParams(window.location.search)
        if (!!urlParams.get('selling_plan')) {
          state.selectedSellingPlanId = +urlParams.get('selling_plan')
          state.selectedFreq = 'subscribe'
        }

        if (!!urlParams.get('bundle[]')) {
          const bundleItems = urlParams.getAll('bundle[]')
          const selections = bundleItems.map(bundleItem => {
            const id = bundleItem.split(':')[0]
            const packCans = +bundleItem.split(':')[1]

            const product = products.find(p => {
              const vids = p.variants.map(v => v.id)
              return vids.includes(+id)
            })
            const variant = product.variants.find(v => v.id === +id)

            return {
              id: `${product.id}`,
              variantId: `${variant.id}`,
              variantGqlId: `gid://shopify/ProductVariant/${variant.id}`,
              title: product.title,
              image: product.featured_image,
              price: variant.price,
              qty: packCans / variant.pack_size,
              packSize: variant.pack_size
            }
          })
          state.selections = selections
        }

        // ─── Analytics: bundle_start / bundle_cart_modification ───
        const entryPoint = new URLSearchParams(window.location.search).get('entry') || 'direct';
        _bundleStartTime = Date.now();

        if (new URLSearchParams(window.location.search).get('bundle_id')) {
          trackEvent('bundle_cart_modification', {
            bundle_id: new URLSearchParams(window.location.search).get('bundle_id'),
            pack_size: state.packCans,
          });
        } else {
          trackEvent('bundle_start', { entry_point: entryPoint });
        }

        // Abandon tracking — fires when user navigates away mid-build
        document.addEventListener('visibilitychange', function () {
          if (document.visibilityState === 'hidden' && filledCount() > 0 && !isFull()) {
            trackEvent('bundle_abandoned_midway', {
              filled_slots: filledCount(),
              total_slots: state.packCans,
              pack_size: state.packCans,
            });
          }
        });

        // Render products
        renderProducts(products);
        bindProductCards();

        const cards = document.querySelectorAll('.bb-product-card[data-product-id]');
        for (const card of cards) {
          syncCard(card)
        }

        updateAddButtons()

        // Init pack size selector
        renderPackSizes();

        // Init frequency UI
        initFrequency();

        // Initial render
        renderSlots();
        updateProgress();
      })
      .catch(function (err) {
        console.error('[BundleBuilder] Failed to load bundle:', err);
        const loading = document.getElementById('bb-loading');
        if (loading) {
          const text = loading.querySelector('.bb-loading-text');
          if (text) text.textContent = 'Failed to load bundle. Please refresh.';
          const spinner = loading.querySelector('.bb-loading-spinner');
          if (spinner) spinner.style.display = 'none';
        }
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
